import { Button } from "@/shared/ui/Button";
import { formatAmount, getAllocationRoom } from "./utils";

type AllocationRoomHintProps = {
  /** `solde_affectation` de l'opération, servi par le back, jamais recalculé ici. */
  operationBalance: number | null
  /** L'écriture en cours d'édition, telle qu'elle est enregistrée. */
  allocation: { debit: number | null, credit: number | null } | null
  /** Écrit le maximum dans le bon champ, et vide l'autre. */
  onApply: (debit: string, credit: string) => void
  disabled?: boolean
};

/**
 * Ce que l'opération porte encore, montré à l'endroit où l'élu saisit le montant.
 *
 * Le défaut qu'elle ferme, mesuré en production : un élu affecte un virement à ses
 * opérations et saisit le PRIX AFFICHÉ au lieu de la PART SALARIÉ — 170,00 € sur
 * une opération qui vaut 75,00 €, l'écart étant la subvention CSE. Le garde de
 * l'API refuse désormais l'écriture, mais un refus arrive après la faute : ce
 * qu'on montre ici la rend inutile à commettre.
 *
 * Les drawers de recherche font déjà ce travail au moment du CHOIX de l'opération.
 * Il manquait à l'ÉDITEUR, où le montant reste librement modifiable après coup —
 * et c'est par là que la faute passe encore : une saisie de 64,00 € sur une
 * opération de 63,00 €, le 2026-04-03, est postérieure au pré-remplissage des
 * drawers.
 *
 * DEUX NOMBRES, ET ILS NE DISENT PAS LA MÊME CHOSE. « Solde de l'opération » est
 * la valeur servie telle quelle ; « Maximum sur cette écriture » lui rend la
 * contribution de la ligne en cours (cf. `getAllocationRoom`). Les libellés sont
 * volontairement dissemblables : côte à côte, deux nombres proches se lisent l'un
 * pour l'autre.
 */
const AllocationRoomHint = ({
  operationBalance, allocation, onApply, disabled,
}: AllocationRoomHintProps) => {
  const room = getAllocationRoom(operationBalance, allocation);
  const side = room >= 0 ? "crédit" : "débit";
  const amount = formatAmount(Math.abs(room));
  const isSettled = Math.abs(room) < 0.005;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
      <span>
        Solde de l&apos;opération :
        {" "}
        <span className="tabular-nums font-medium text-slate-800">
          {formatAmount(operationBalance ?? 0)}
          {" "}
          &euro;
        </span>
      </span>
      <span className="text-slate-300">|</span>
      {isSettled
        ? (
          <span className="font-medium text-slate-800">
            Opération soldée : plus rien à y affecter
          </span>
        )
        : (
          <>
            <span>
              Maximum sur cette écriture :
              {" "}
              <span className="tabular-nums font-medium text-slate-800">
                {amount}
                {" "}
                &euro;
              </span>
              {" "}
              au
              {" "}
              {side}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              disabled={disabled}
              onClick={() => (room >= 0 ? onApply("", amount) : onApply(amount, ""))}
            >
              Reprendre
            </Button>
          </>
        )}
    </div>
  );
};

export default AllocationRoomHint;
